import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Search from './components/Search';
import Auth from './components/auth/Auth';
import Profile from './components/auth/Profile';
import AdminPage from './components/admin/AdminPage';
import SearchHistory from './components/SearchHistory';
import MassUpload from './components/MassUpload';
import Monitoring from './components/Monitoring';
import CaseManagement from './components/CaseManagement';
import Sidebar from './components/Sidebar';
import VerifyEmail from './components/VerifyEmail';
import TrialBanner from './components/TrialBanner';
import ProtectedComponent from './components/ProtectedComponent';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import OrganizationSetup from './components/auth/OrganizationSetup';
import OrgAdminPage from './components/orgAdmin/OrgAdminPage';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import { Shield, Globe, Sun, Moon, AlertTriangle } from 'lucide-react';
import LanguageSwitcher from './components/LanguageSwitcher';
import { useTranslation } from './hooks/useTranslation';
import CookieConsent from "react-cookie-consent";
import { removeAllCookies } from './utils/cookieUtils';
import AuthHandler from './pages/AuthHandler';
import PastDueBanner from './components/PastDueBanner';
import Payments from './components/Payments';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import { httpsCallable, getFunctions } from 'firebase/functions';

// Theme Switcher Component
function ThemeSwitcher() {
  const { theme, toggleTheme } = useTheme();
  const t = useTranslation();
  
  return (
    <button
      className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
      onClick={toggleTheme}
      title={theme === 'dark' ? t('themes.light') : t('themes.dark')}
      aria-label={theme === 'dark' ? t('themes.light') : t('themes.dark')}
    >
      {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
}

// Language Loading Component
function LanguageLoader() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-900 z-50">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 dark:border-blue-400 mb-4"></div>
        <p className="text-slate-700 dark:text-slate-300 text-lg font-medium">Loading your preferred language...</p>
      </div>
    </div>
  );
}

// Main application component for authenticated and verified users
function AuthenticatedApp() {
  const { currentUser, currentOrg, userRole, isOrgAdmin, isGlobalAdmin, isPastDue, daysPastDue } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const headerRef = useRef(null);
  const [headerSearchVisible, setHeaderSearchVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const t = useTranslation();
  const { theme } = useTheme(); // Get theme for conditional styling if needed
  
  // Determine active page based on URL
  const getActivePageFromPath = (path) => {
    if (path === '/') return 'home';
    return path.substring(1); // remove leading slash
  };
  
  const activePage = getActivePageFromPath(location.pathname);
  
  // Effect to auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) { // Use Tailwind's breakpoint 'md' equivalent
        setSidebarCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Function to toggle profile
  const toggleProfile = () => {
    if (location.pathname === '/profile') {
      navigate('/');
    } else {
      navigate('/profile');
    }
  };
  
  // Function to reset search and go to home
  const resetSearch = () => {
    navigate('/');
    if (searchRef.current && typeof searchRef.current.resetSearch === 'function') {
      searchRef.current.resetSearch();
    }
  };
  
  // Handle sidebar navigation
  const handleNavigate = (page) => {
    navigate(`/${page === 'home' ? '' : page}`);
    if (page === 'home' && searchRef.current && typeof searchRef.current.resetSearch === 'function') {
      searchRef.current.resetSearch();
    }
  };
  
  // Handle sidebar collapse toggle
  const handleSidebarToggle = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  // Handle header search visibility with hysteresis to prevent flickering
  useEffect(() => {
    const handleScroll = () => {
      if (activePage === 'home') {
        // Use different thresholds for showing vs hiding to prevent flickering
        const scrollY = window.scrollY;
        setHeaderSearchVisible(prev => {
          if (!prev && scrollY > 120) {
            // Show when scrolling down past 120px
            return true;
          } else if (prev && scrollY < 80) {
            // Hide when scrolling up past 80px
            return false;
          }
          // Keep current state when between 80px and 120px
          return prev;
        });
      } else {
        setHeaderSearchVisible(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activePage]);
  
  // --- Past Due Overlay Logic ---
  useEffect(() => {
    if (isPastDue && daysPastDue >= 7) {
      setShowPayments(true);
    } else {
      setShowPayments(false);
    }
  }, [isPastDue, daysPastDue]);

  // --- Payment Success Modal Logic ---
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isPaymentSuccess = urlParams.get('success') === 'true';
    const isTestSuccess = urlParams.get('test-success') === 'true'; // For testing purposes
    
    if (isPaymentSuccess || isTestSuccess) {
      setShowPaymentSuccess(true);
      // Clean up the URL by removing the success/test-success parameter
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('success');
      newUrl.searchParams.delete('test-success');
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  // Handle payment success modal close
  const handlePaymentSuccessClose = () => {
    setShowPaymentSuccess(false);
  };

  // Check if user needs to set up organization
  if (!currentOrg) {
    return <OrganizationSetup />;
  }

  // --- Organization-level Stripe Portal Handler ---
  const handleResolvePayment = async () => {
    // Check if user is org admin
    if (!isOrgAdmin) {
      alert(t('profile.contactAdmin', 'Contact your organization admin to manage billing and subscriptions.'));
      return;
    }

    try {
      // Ensure currentUser is available
      if (!currentUser) {
        console.error('No user found');
        alert('Please log in to access payment portal');
        return;
      }

      // Get authentication token
      const token = await currentUser.getIdToken(true);
      
      // Cloud function URL
      const region = 'us-central1'; // or your specific region
      const projectId = 'sanctions-screener-20d59'; // your Firebase project ID
      const functionUrl = `https://${region}-${projectId}.cloudfunctions.net/createPortalSessionSimple`;
      
      // Make direct fetch call to the function
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          returnUrl: window.location.origin + '/'
        })
      });
      
      // Handle response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', response.status, errorData);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Redirect to the portal URL
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      // Show a meaningful error
      alert('Unable to access payment portal. Please contact support.');
    }
  };
  
  return (
    // Base App container with background colors and text defaults
    <div className="flex flex-col min-h-screen bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      {/* Blocking overlay for hard lock if past due >= 7 days - SIMPLIFIED */}
      {isPastDue && daysPastDue >= 7 && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-lg shadow-xl p-8 flex flex-col items-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-300" />
            </div>
            <h2 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-4">
              {t('pastdue.modal.title') || 'Payment Overdue'}
            </h2>
            <p className="mb-6 text-gray-700 dark:text-gray-300 text-center">
              {isOrgAdmin 
                ? (t('pastdue.modal.message') || 'Your subscription payment is overdue. You cannot use the app until your payment is resolved.')
                : (t('profile.contactAdmin', 'Contact your organization admin to manage billing and subscriptions.') + ' The app cannot be used until payment is resolved.')
              }
            </p>
            {isOrgAdmin && (
              <button
                className="px-6 py-3 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 transition-colors"
                onClick={handleResolvePayment}
              >
                {t('pastdue.modal.button') || 'Update Payment Method'}
              </button>
            )}
            {currentOrg?.name && (
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 text-center">
                <span className="font-medium">{t('common.organization', 'Organization')}:</span> {currentOrg.name}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      {showPaymentSuccess && (
        <PaymentSuccessModal onClose={handlePaymentSuccessClose} />
      )}

      {/* Sticky Header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ease-in-out ${headerSearchVisible ? 'h-14 shadow-md' : 'h-20'} bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white`}
        ref={headerRef}
      >
        {/* Removed decorative blobs for simplicity, can be added back with custom CSS/SVG if needed */}
        
        {/* Header Content Container */}
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          {/* Title/Logo Section */}
          <div 
            className="flex items-center cursor-pointer group" 
            onClick={resetSearch}
            title={t('common.home')}
            role="button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') resetSearch();
            }}
          >
            <img 
              src={theme === 'dark' ? "/Sanction_Screener_dark.png" : "/Sanction_Screener_light.png"} 
              alt="Sanction Screener" 
              className="h-12 mr-3 transition-all duration-300 ease-in-out"
              style={{ maxHeight: headerSearchVisible ? '40px' : '48px' }}
            />
          </div>
          
          {/* Header Search Bar - Visible when scrolled on home */}
          {currentUser && activePage === 'home' && (
            <div className={`absolute left-1/2 transform -translate-x-1/2 transition-opacity duration-300 ${headerSearchVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
              {searchRef.current && (
                <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-full shadow-sm px-2 py-1 w-96">
                  <input
                    type="text"
                    placeholder={t('search.placeholder')}
                    value={searchRef.current.getSearchTerm ? searchRef.current.getSearchTerm() : ''}
                    onChange={(e) => searchRef.current.setSearchTerm && searchRef.current.setSearchTerm(e.target.value)}
                    className="flex-grow bg-transparent text-slate-700 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400 px-3 py-1 focus:outline-none sm:placeholder-opacity-100 placeholder-opacity-0"
                  />
                  <button 
                    className="ml-2 px-4 py-1 bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 rounded-full text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500"
                    onClick={() => searchRef.current.handleSearch && searchRef.current.handleSearch()}
                  >
                    {t('search.button')}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* User Controls Section */}
          {currentUser && (
            <div className="flex items-center space-x-3">
              {/* Welcome Message - Hidden on small screens and when search is visible */}
              <span className={`text-sm transition-opacity duration-300 ${headerSearchVisible ? 'opacity-0 hidden md:inline' : 'opacity-100'} hidden md:inline`}>
                {t('common.welcome')}, {currentUser.displayName || currentUser.email}
              </span>
              
              <ThemeSwitcher />
              <LanguageSwitcher />
              {/* Profile Button */}
              <button 
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-300 dark:focus:ring-offset-slate-900 ${
                  location.pathname === '/profile' 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white ring-2 ring-purple-400 dark:ring-pink-400' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                }`}
                onClick={toggleProfile}
                title={location.pathname === '/profile' ? t('common.search') : t('common.profile')}
              >
                <span className="font-semibold text-sm select-none">
                  {currentUser.displayName 
                    ? currentUser.displayName.split(' ').map(name => name[0]).join('').substring(0, 2).toUpperCase()
                    : currentUser.email.substring(0, 2).toUpperCase()}
                </span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Layout (Sidebar + Content) */}
      <div className="flex flex-grow">
        <Sidebar 
          activePage={activePage}
          onNavigate={handleNavigate}
          userRole={userRole}
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleSidebarToggle}
          // Pass theme if Sidebar needs it for styling
        />
        
        {/* Main Content Area - Correct margin, restore transition */}
        <div className={`flex-grow flex flex-col transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
          {/* Past Due Banner (above TrialBanner) */}
          {isPastDue && daysPastDue < 7 && (
            <PastDueBanner daysPastDue={daysPastDue} onResolve={handleResolvePayment} />
          )}
          {/* Trial Banner - Placed inside */}
          {!bannerDismissed && (
            <TrialBanner onDismiss={() => setBannerDismissed(true)} />
          )}
          
          {/* Main Content */}
          <main className="p-4 md:p-6 lg:p-8"> 
            {/* Routes are now implicitly protected by AppContent logic */}
            <Routes>
              <Route path="/" element={<Search ref={searchRef} />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/verification/:id" element={<Search ref={searchRef} displayMode="verification" />} />
              {/* Global Admin route protected with ProtectedComponent */}
              <Route path="/super-admin" element={
                <ProtectedComponent 
                  requiredRole="global-admin" 
                  fallback={<div className="p-4 text-red-500">You don't have permission to access this page.</div>}
                >
                  <AdminPage />
                </ProtectedComponent>
              } />
              {/* Organization Admin route */}
              <Route path="/org-admin" element={
                <ProtectedComponent 
                  requiredRole="org-admin" 
                  fallback={<div className="p-4 text-red-500">You don't have permission to access this page.</div>}
                >
                  <OrgAdminPage />
                </ProtectedComponent>
              } />
              <Route path="/history" element={<SearchHistory />} />
              {/* MassUpload requires Professional subscription */}
              <Route path="/massUpload" element={
                <ProtectedComponent 
                  requiredSubscription="professional"
                  fallback={
                    <div className="p-6">
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200">
                              Professional Subscription Required
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                              <p className="mb-2">
                                Mass Upload and Batch Screening are premium features available with Professional and Enterprise subscriptions.
                              </p>
                              <p>
                                Upgrade your subscription to access bulk screening capabilities and process multiple names at once.
                              </p>
                            </div>
                            <div className="mt-4">
                              <button
                                onClick={() => window.location.href = '/org-admin'}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                              >
                                Upgrade Subscription
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <MassUpload />
                </ProtectedComponent>
              } />
              <Route path="/monitoring" element={<Monitoring />} />
              <Route path="/cases" element={<CaseManagement />} />
              <Route path="/search/:id" element={<Search ref={searchRef} displayMode="case-management" />} />
              <Route path="/auth" element={<AuthHandler />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const { currentUser, loading, initialLoad, userRole } = useAuth();
  const { isLoading } = useLanguage(); // Get language loading state
  const [showLoading, setShowLoading] = useState(true); 
  const navigate = useNavigate(); 
  const hasRedirected = useRef(false);
  const location = useLocation();
  
  // Use theme for consistent styling
  const { theme } = useTheme();
  
  const isOAuthProvider = () => {
    if (!currentUser || !currentUser.providerData || currentUser.providerData.length === 0) {
      return false;
    }
    const providerId = currentUser.providerData[0].providerId;
    return providerId === 'microsoft.com' || providerId === 'google.com';
  };
  
  useEffect(() => {
    let timer;
    if (!loading && !initialLoad) {
      timer = setTimeout(() => setShowLoading(false), 300); 
    } else {
      setShowLoading(true); 
    }
    return () => clearTimeout(timer);
  }, [loading, initialLoad]);
  
  useEffect(() => {
    if (currentUser && !loading && !initialLoad && !hasRedirected.current) {
      if (isOAuthProvider() || currentUser.emailVerified) {
        // Only navigate to home if we're on the auth page or no specific route
        const isAuthPage = location.pathname === '/auth';
        const isDefaultRoute = location.pathname === '/';
        
        if (isAuthPage || (!isDefaultRoute && location.pathname === '')) {
          navigate('/');
        }
        hasRedirected.current = true;
      }
    }
    if (!currentUser) {
      hasRedirected.current = false;
    }
  }, [currentUser, loading, initialLoad, navigate, location.pathname]);
  
  // Apply theme class to html element for Tailwind dark mode
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove(theme === 'dark' ? 'light' : 'dark');
    root.classList.add(theme);
  }, [theme]);
  
  // If language is still loading, show language loader
  if (isLoading) {
    return <LanguageLoader />;
  }
  
  // Existing loading checks for authentication
  if (showLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Auth />;
  }
  
  if (!currentUser.emailVerified && !isOAuthProvider()) {
    return <VerifyEmail />; 
  }
  
  // If we got here, user is authenticated and verified
  return <AuthenticatedApp />;
}

function App() {
  const { theme } = useTheme();
  const t = useTranslation();
  
  // Define cookie consent styling based on theme
  const cookieStyle = {
    maxWidth: "580px",
    margin: "0 auto 20px auto", 
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    padding: "15px 20px",
    background: theme === 'dark' ? "#1e293b" : "#ffffff",
    color: theme === 'dark' ? "#ffffff" : "#1e293b",
    border: theme === 'dark' ? "none" : "1px solid #e2e8f0"
  };
  
  const acceptButtonStyle = {
    color: "#ffffff",
    background: "#4f46e5",
    fontSize: "14px",
    borderRadius: "0.375rem",
    padding: "0.5rem 1rem",
    margin: "0 0 0 15px",
    border: "none"
  };
  
  const declineButtonStyle = {
    color: theme === 'dark' ? "#ffffff" : "#1e293b",
    background: theme === 'dark' ? "#475569" : "#e2e8f0",
    fontSize: "14px",
    borderRadius: "0.375rem",
    padding: "0.5rem 1rem",
    border: "none",
    margin: "0 10px 0 10px"
  };
  
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <ToastProvider>
            <AppContent />
            <CookieConsent
              location="bottom"
              buttonText={t('cookies.accept')}
              declineButtonText={t('cookies.deny')}
              enableDeclineButton
              cookieName="protegoCookieConsent"
              declineCookieValue="false"
              sameSite="strict"
              path="/"
              onDecline={() => {
                removeAllCookies();
                console.log('User declined cookies');
              }}
              onAccept={() => {
                console.log('User accepted cookies');
              }}
              style={cookieStyle}
              buttonStyle={acceptButtonStyle}
              declineButtonStyle={declineButtonStyle}
              contentStyle={{
                flex: "1",
                margin: "0",
                padding: "0",
                display: "flex",
                alignItems: "center"
              }}
              expires={150}
              hideOnDecline={true}
              hideOnAccept={true}
              cookieValue={true}
              disableButtonStyles={false}
              enableStorage={true}
              storageMethod="cookie"
            >
              <div>
                {t('cookies.consent')}{" "}
                <a 
                  href="https://sanctionscreener.eu/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "underline",
                    color: theme === 'dark' ? "#ffffff" : "#1e293b"
                  }}
                >
                  {t('cookies.privacyPolicy')}
                </a>
              </div>
            </CookieConsent>
          </ToastProvider>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;